/**
 * Pure helpers for the Appearance builder (no React): colour strings, immutable
 * edits of the page document, section labels and mapping server error paths
 * (sections[3].props.items[0].q) back to the section that has them.
 * Storefront Studio (B15): edits of the whole store document — template pages
 * (blocks that respect `required`), header/footer and card — and error paths
 * (pages.product.blocks[2].props.title) mapped back to the area and block.
 */
import { SECTION_TYPES, newSection, STORE_PAGES, CONTENT_BLOCKS, MAX_BLOCKS, blockDef, newBlock, templatePage } from '@dreamtree-org/landing-kit/schema';

/** ColorPicker gives rgba()/hex strings; the theme stores #rrggbb. */
export function toHex(value) {
  const s = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) return `#${s.slice(1).split('').map((c) => c + c).join('')}`.toLowerCase();
  const m = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i.exec(s);
  if (!m) return null;
  return `#${m.slice(1, 4).map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('')}`;
}

/** Set `value` at `path` (array of keys/indexes), copying only along the path. */
export function setIn(obj, path, value) {
  if (!path.length) return value;
  const [head, ...rest] = path;
  const copy = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  copy[head] = setIn(copy[head], rest, value);
  return copy;
}

export const sectionTypeLabel = (type) => (SECTION_TYPES[type] ? SECTION_TYPES[type].label : type);

/** What a section is called in the list: its heading, else its type. */
export function sectionLabel(section) {
  const p = section.props || {};
  const first = Array.isArray(p.slides) && p.slides[0] ? p.slides[0].heading : null;
  return (p.title || p.heading || first || '').trim() || sectionTypeLabel(section.type);
}

export function addSection(page, type, index = page.sections.length) {
  const s = newSection(type);
  const sections = [...page.sections];
  sections.splice(index, 0, s);
  return { page: { ...page, sections }, section: s };
}

export function duplicateSection(page, id) {
  const i = page.sections.findIndex((s) => s.id === id);
  if (i < 0) return page;
  const src = page.sections[i];
  const copy = { ...JSON.parse(JSON.stringify(src)), id: newSection(src.type).id };
  const sections = [...page.sections];
  sections.splice(i + 1, 0, copy);
  return { ...page, sections };
}

export const removeSection = (page, id) => ({ ...page, sections: page.sections.filter((s) => s.id !== id) });

export const updateSection = (page, id, patch) => ({ ...page, sections: page.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

/** Server / validator errors grouped by section id (with the field path inside the section). */
export function errorsBySection(page, errors = []) {
  const out = {};
  for (const e of errors) {
    const m = /^sections\[(\d+)\](?:\.props\.)?(.*)$/.exec(e.path || '');
    const s = m && page.sections[Number(m[1])];
    const key = s ? s.id : '_page';
    (out[key] = out[key] || []).push({ path: m ? m[2] : e.path, message: e.message });
  }
  return out;
}

/** Field errors for one field path inside a section ("slides[0].heading"). */
export const errorAt = (list = [], path) => (list.find((e) => e.path === path) || {}).message;

/* ---------------- Storefront Studio: the store document ---------------- */

/** The editor's areas, in tab order (each area's panel and preview: components/appearance/areas.jsx). */
export const STUDIO_AREAS = [
  { value: 'home', label: 'Home' },
  { value: 'listing', label: 'Listing' },
  { value: 'product', label: 'Product' },
  { value: 'chrome', label: 'Header & footer' },
  { value: 'card', label: 'Product card' },
  { value: 'theme', label: 'Theme' },
];

export const setHome = (doc, home) => ({ ...doc, pages: { ...doc.pages, home } });
const setPage = (doc, pageKey, page) => ({ ...doc, pages: { ...doc.pages, [pageKey]: page } });
const isRequired = (pageKey, type) => !!(STORE_PAGES[pageKey].slots[type] && STORE_PAGES[pageKey].slots[type].required);
export { isRequired };

/** What a block is called in the list: a content section's heading, else the block's label. */
export function blockLabel(pageKey, block) {
  const def = blockDef(pageKey, block.type);
  if (def && !def.slot) return sectionLabel(block);
  const title = block.props && typeof block.props.title === 'string' ? block.props.title.trim() : '';
  return title || (def ? def.label : block.type);
}

/**
 * Switch a page's template: the template's settings and block order, keeping the
 * owner's slot options and any content sections they added (after the template's blocks).
 */
export function applyTemplate(doc, pageKey, template) {
  const old = doc.pages[pageKey] || { blocks: [] };
  const next = templatePage(pageKey, template);
  const oldByType = Object.fromEntries((old.blocks || []).filter((b) => blockDef(pageKey, b.type)?.slot).map((b) => [b.type, b]));
  const blocks = next.blocks.map((b) => (oldByType[b.type] ? { ...b, props: { ...b.props, ...oldByType[b.type].props } } : b));
  const kept = (old.blocks || []).filter((b) => !blockDef(pageKey, b.type)?.slot && !blocks.some((x) => x.id === b.id));
  return setPage(doc, pageKey, { ...next, blocks: [...blocks, ...kept].slice(0, MAX_BLOCKS) });
}

export const setSettings = (doc, pageKey, settings) => setPage(doc, pageKey, { ...doc.pages[pageKey], settings });

/** Reorder (Sortable gives the whole new list). */
export const setBlocks = (doc, pageKey, blocks) => setPage(doc, pageKey, { ...doc.pages[pageKey], blocks });

/** Patch one block; a required block cannot be hidden. */
export function updateBlock(doc, pageKey, id, patch) {
  const blocks = doc.pages[pageKey].blocks.map((b) => {
    if (b.id !== id) return b;
    const next = { ...b, ...patch };
    if (isRequired(pageKey, b.type)) next.enabled = true;
    return next;
  });
  return setBlocks(doc, pageKey, blocks);
}

/** Remove a block unless it is required. */
export function removeBlock(doc, pageKey, id) {
  const b = doc.pages[pageKey].blocks.find((x) => x.id === id);
  if (!b || isRequired(pageKey, b.type)) return doc;
  return setBlocks(doc, pageKey, doc.pages[pageKey].blocks.filter((x) => x.id !== id));
}

/** Blocks the owner can add: slots not on the page yet, then any content section (none once the page is full). */
export function addableBlocks(doc, pageKey) {
  const blocks = doc.pages[pageKey].blocks;
  if (blocks.length >= MAX_BLOCKS) return [];
  const slots = Object.entries(STORE_PAGES[pageKey].slots).filter(([type]) => !blocks.some((b) => b.type === type)).map(([type, d]) => ({ type, label: d.label, description: d.description, slot: true }));
  // A section type that shares a slot's name (product `gallery`) resolves to the slot, so it is not offered there.
  const content = CONTENT_BLOCKS.filter((type) => !STORE_PAGES[pageKey].slots[type]).map((type) => ({ type, label: SECTION_TYPES[type].label, description: SECTION_TYPES[type].description, slot: false }));
  return [...slots, ...content];
}

/** Add a block before the last one (the results grid or similar products usually close a page). */
export function addBlock(doc, pageKey, type) {
  const b = newBlock(pageKey, type);
  const blocks = [...doc.pages[pageKey].blocks];
  if (blocks.some((x) => x.id === b.id)) return { doc, block: null };
  blocks.splice(Math.max(0, blocks.length - 1), 0, b);
  return { doc: setBlocks(doc, pageKey, blocks), block: b };
}

/** Header/footer and card edits. */
export const setChrome = (doc, chrome) => ({ ...doc, chrome });
export const setCard = (doc, card) => ({ ...doc, card });

/**
 * Store-document errors (validateStore or the server's 422 details) by area:
 * home → errors with `sections[...]` paths (for errorsBySection), listing/product
 * → { [blockId]: [...], _settings: [...], _page: [...] }, chrome/card → field paths.
 */
export function errorsByArea(doc, errors = []) {
  const out = { home: [], listing: {}, product: {}, chrome: [], card: [], theme: [], other: [] };
  for (const e of errors) {
    const path = e.path || '';
    let m;
    if ((m = /^pages\.home\.(.*)$/.exec(path))) out.home.push({ ...e, path: m[1] });
    else if ((m = /^pages\.(listing|product)\.blocks\[(\d+)\](?:\.props\.)?(.*)$/.exec(path))) {
      const b = doc.pages[m[1]] && doc.pages[m[1]].blocks[Number(m[2])];
      const key = b ? b.id : '_page';
      (out[m[1]][key] = out[m[1]][key] || []).push({ path: m[3], message: e.message });
    } else if ((m = /^pages\.(listing|product)\.settings\.(.*)$/.exec(path))) (out[m[1]]._settings = out[m[1]]._settings || []).push({ path: m[2], message: e.message });
    else if ((m = /^pages\.(listing|product)(?:\.(.*))?$/.exec(path))) (out[m[1]]._page = out[m[1]]._page || []).push({ path: m[2] || '', message: e.message });
    else if ((m = /^chrome\.(.*)$/.exec(path))) out.chrome.push({ path: m[1], message: e.message });
    else if ((m = /^card\.(.*)$/.exec(path))) out.card.push({ path: m[1], message: e.message });
    else if (/^theme(\.|$)/.test(path)) out.theme.push(e);
    else out.other.push(e);
  }
  return out;
}

/** How many problems an area has (for the tab badges). */
export function areaProblemCount(byArea, area) {
  const v = byArea[area];
  if (!v) return 0;
  return Array.isArray(v) ? v.length : Object.values(v).reduce((n, list) => n + list.length, 0);
}

const CHROME_PARTS = { announcement: 'Announcement bar', header: 'Header', footer: 'Footer' };

/**
 * Where a pre-publish QA warning is (MERCH-55, lib/store/storefrontQa paths), as
 * "Area · part" for the publish check dialog: sections[2]… → "Home · <section>",
 * pages.listing.blocks[1]… → "Listing · <block>", chrome.footer… → "Header & footer · Footer".
 */
export function warningPlace(doc, path = '') {
  const label = (v) => (STUDIO_AREAS.find((a) => a.value === v) || {}).label || v;
  let m;
  if ((m = /^sections\[(\d+)\]/.exec(path))) {
    const s = doc && doc.pages && doc.pages.home.sections[Number(m[1])];
    return s ? `${label('home')} · ${sectionLabel(s)}` : label('home');
  }
  if ((m = /^pages\.(listing|product)\.blocks\[(\d+)\]/.exec(path))) {
    const b = doc && doc.pages && doc.pages[m[1]] && doc.pages[m[1]].blocks[Number(m[2])];
    return b ? `${label(m[1])} · ${blockLabel(m[1], b)}` : label(m[1]);
  }
  if ((m = /^chrome\.(\w+)/.exec(path))) return `${label('chrome')}${CHROME_PARTS[m[1]] ? ` · ${CHROME_PARTS[m[1]]}` : ''}`;
  // Live catalogue warnings (MERCH-88: size charts, colour swatches) name a listing, not a part of the page.
  if ((m = /^catalog\.listings\[(\d+)\]/.exec(path))) return `Products · listing ${m[1]}`;
  return 'Storefront';
}
